import React, { useMemo, useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  isSameDay,
  startOfDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Landmark, Calendar as CalendarIcon } from "lucide-react";
import { AssetLogo } from "@/components/asset/asset-logo";

export type CalendarEventType = "dividend" | "earnings";
type CalendarEventTone = "dividendPayment" | "exDividend" | "earnings";

export interface CalendarEvent {
  id: string;
  date: Date;
  title: string;
  subTitle?: string;
  type: CalendarEventType;
  assetSymbol?: string;
  assetName?: string;
  assetType?: string;
  assetWebsite?: string;
  color?: string;
  amount?: number;
  currency?: string;
  estimate?: number;
  actual?: number;
}

interface UnifiedCalendarProps {
  viewDate: Date;
  viewMode: "month" | "week";
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
}

function getEventTone(event: CalendarEvent): CalendarEventTone {
  if (event.type === "earnings") return "earnings";
  return event.subTitle === "Payment" ? "dividendPayment" : "exDividend";
}

function getEventToneLabel(event: CalendarEvent): string {
  const tone = getEventTone(event);
  if (tone === "dividendPayment") return "Dividend payment";
  if (tone === "exDividend") return "Ex-dividend";
  return "Earnings";
}

function hasMeaningfulTime(date: Date): boolean {
  return (
    date.getUTCHours() !== 0 ||
    date.getUTCMinutes() !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCMilliseconds() !== 0
  );
}

function formatEventTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function getEventToneClasses(event: CalendarEvent) {
  const tone = getEventTone(event);

  if (tone === "dividendPayment") {
    return {
      card: "bg-emerald-500/10 border-emerald-500/25 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300",
      chip: "bg-emerald-500/15 ring-1 ring-emerald-500/40",
      dot: "bg-emerald-400",
      badge: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      amount: "text-emerald-600 dark:text-emerald-300",
    };
  }

  if (tone === "exDividend") {
    return {
      card: "bg-amber-500/10 border-amber-500/25 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300",
      chip: "bg-amber-500/15 ring-1 ring-amber-500/40",
      dot: "bg-amber-400",
      badge: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
      amount: "text-amber-600 dark:text-amber-300",
    };
  }

  return {
    card: "bg-sky-500/10 border-sky-500/25 text-sky-700 hover:bg-sky-500/20 dark:text-sky-300",
    chip: "bg-sky-500/15 ring-1 ring-sky-500/40",
    dot: "bg-sky-400",
    badge: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    amount: "text-sky-600 dark:text-sky-300",
  };
}

export function UnifiedCalendar({
  viewDate,
  viewMode,
  events,
  onEventClick,
}: UnifiedCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const days = useMemo(() => {
    let start, end;
    if (viewMode === "month") {
      const monthStart = startOfMonth(viewDate);
      const monthEnd = endOfMonth(monthStart);
      start = startOfWeek(monthStart, { weekStartsOn: 1 });
      end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    } else {
      start = startOfWeek(viewDate, { weekStartsOn: 1 });
      end = endOfWeek(start, { weekStartsOn: 1 });
    }
    return eachDayOfInterval({ start, end });
  }, [viewDate, viewMode]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events.forEach((event) => {
      const key = format(startOfDay(event.date), "yyyy-MM-dd");
      if (!map[key]) map[key] = [];
      map[key].push(event);
    });
    return map;
  }, [events]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDate) return [];
    return eventsByDay[format(selectedDate, "yyyy-MM-dd")] || [];
  }, [selectedDate, eventsByDay]);

  return (
    <div className="flex flex-col bg-background border rounded-xl overflow-hidden shadow-sm md:h-full">
      {/* Weekday Header */}
      <div className="grid grid-cols-7 border-b bg-muted/30">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider"
          >
            <span className="hidden md:inline">{day}</span>
            <span className="md:hidden">{day.charAt(0)}</span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className={cn(
        "grid grid-cols-7 overflow-auto md:flex-1",
        viewMode === "month"
          ? "auto-rows-[minmax(56px,auto)] md:auto-rows-fr"
          : "auto-rows-[minmax(92px,auto)] md:min-h-[500px]"
      )}>
        {days.map((day) => {
          const dayKey = format(day, "yyyy-MM-dd");
          const dayEvents = eventsByDay[dayKey] || [];
          const isCurrentMonth = isSameMonth(day, viewDate);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          
          return (
            <div
              key={day.toString()}
              onClick={() => setSelectedDate(day)}
              className={cn(
                "min-h-14 md:min-h-[120px] p-1 md:p-2 border-r border-b last:border-r-0 flex flex-col gap-0.5 transition-colors cursor-pointer",
                !isCurrentMonth && "bg-muted/10 opacity-40",
                isToday(day) && "bg-primary/5",
                isSelected && "ring-2 ring-primary ring-inset bg-primary/5"
              )}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className={cn(
                  "text-[10px] md:text-xs font-medium h-5 w-5 md:h-6 md:w-6 flex items-center justify-center rounded-full",
                  isToday(day) ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                )}>
                  {format(day, "d")}
                </span>
                {/* Mobile: event count badge */}
                {dayEvents.length > 0 && (
                  <span className="md:hidden text-[9px] font-medium text-muted-foreground bg-muted rounded-full h-4 min-w-4 flex items-center justify-center px-1">
                    {dayEvents.length}
                  </span>
                )}
              </div>

              {/* Desktop: full event cards */}
              <div className="hidden md:flex flex-col gap-1 flex-1 overflow-y-auto no-scrollbar">
                {dayEvents.map((event) => (
                  <DesktopEventCard key={event.id} event={event} onClick={() => onEventClick?.(event)} />
                ))}
              </div>

              {/* Mobile: compact logo chips */}
              <div className="md:hidden flex flex-wrap gap-0.5 overflow-hidden">
                {dayEvents.slice(0, 3).map((event) => (
                  <MobileEventChip key={event.id} event={event} />
                ))}
                {dayEvents.length > 3 && (
                  <span className="text-[8px] text-muted-foreground self-center">+{dayEvents.length - 3}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile Selected Date Detail Panel */}
      <div className="md:hidden border-t flex-shrink-0 max-h-[45vh] overflow-y-auto">
        <div className="p-3 border-b bg-muted/10 sticky top-0">
          <h3 className="font-semibold text-xs text-muted-foreground flex items-center gap-1.5">
            <CalendarIcon className="h-3.5 w-3.5" />
            {selectedDate ? format(selectedDate, "PPPP") : "Tap a day to see details"}
          </h3>
        </div>
        <div className="flex flex-col divide-y">
          {selectedDayEvents.length > 0 ? (
            selectedDayEvents.map(event => (
              <MobileEventDetail key={event.id} event={event} />
            ))
          ) : (
            <p className="text-sm text-muted-foreground italic p-4 text-center">
              {selectedDate ? "No events for this date." : "Select a day to view events."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */
/* Desktop Event Card (inside calendar cell)              */
/* ─────────────────────────────────────────────────────── */
function DesktopEventCard({ event, onClick }: { event: CalendarEvent; onClick?: () => void }) {
  const toneClasses = getEventToneClasses(event);
  
  const mockAsset = {
    type: event.assetType || "stock",
    symbol: event.assetSymbol || "",
    website: event.assetWebsite,
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className={cn(
            "group px-1.5 py-1 rounded-md text-[10px] cursor-pointer transition-all border flex items-center gap-1.5 min-w-0",
            toneClasses.card
          )}
        >
          <div className="flex-shrink-0">
            <AssetLogo asset={mockAsset} size="xs" showFallbackIcon className="rounded-sm" />
          </div>
          <div className="flex flex-col min-w-0 flex-1 leading-none gap-px">
            <span className="font-bold truncate">{event.assetSymbol}</span>
            {event.assetName && (
              <span className="text-[8px] opacity-60 truncate leading-tight">{event.assetName}</span>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <EventPopoverContent event={event} />
    </Popover>
  );
}

/* ─────────────────────────────────────────────────────── */
/* Mobile Event Chip (tiny logo inside calendar cell)     */
/* ─────────────────────────────────────────────────────── */
function MobileEventChip({ event }: { event: CalendarEvent }) {
  const toneClasses = getEventToneClasses(event);
  const mockAsset = {
    type: event.assetType || "stock",
    symbol: event.assetSymbol || "",
    website: event.assetWebsite,
  };

  return (
    <div
      className={cn("relative flex-shrink-0 rounded-sm p-px", toneClasses.chip)}
      title={getEventToneLabel(event)}
    >
      <AssetLogo asset={mockAsset} size="xs" showFallbackIcon className="rounded-sm" />
      <span className={cn("absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-background", toneClasses.dot)} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */
/* Mobile Event Detail (full info in bottom panel)        */
/* ─────────────────────────────────────────────────────── */
function MobileEventDetail({ event }: { event: CalendarEvent }) {
  const isDividend = event.type === "dividend";
  const toneClasses = getEventToneClasses(event);

  const mockAsset = {
    type: event.assetType || "stock",
    symbol: event.assetSymbol || "",
    website: event.assetWebsite,
  };

  return (
    <div className="flex items-start gap-3 p-3">
      {/* Logo */}
      <div className="flex-shrink-0 mt-0.5">
        <AssetLogo asset={mockAsset} size="md" showFallbackIcon className="rounded-lg" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1.5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-sm truncate">{event.assetSymbol}</p>
            {event.assetName && (
              <p className="text-xs text-muted-foreground truncate">{event.assetName}</p>
            )}
          </div>
          <Badge variant="outline" className={cn(
            "capitalize text-[10px] flex-shrink-0",
            toneClasses.badge
          )}>
            {getEventToneLabel(event)}
          </Badge>
        </div>

        {/* Details */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" /> Date
            </span>
            <span className="font-medium">{format(event.date, "PPP")}</span>
          </div>
          {!isDividend && hasMeaningfulTime(event.date) && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Time</span>
              <span className="font-medium">{formatEventTime(event.date)}</span>
            </div>
          )}

          {isDividend ? (
            <>
              {event.subTitle && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{event.subTitle}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Landmark className="h-3 w-3" /> Amount
                </span>
                <span className={cn("font-bold", toneClasses.amount)}>
                  {event.amount !== undefined
                    ? `${event.amount.toFixed(4)} ${event.currency || "USD"}`
                    : "N/A"}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Estimate EPS</span>
                <span className="font-medium">{event.estimate ?? "N/A"}</span>
              </div>
              {event.actual !== undefined && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Actual EPS</span>
                  <span className={cn(
                    "font-bold",
                    (event.actual ?? 0) >= (event.estimate ?? 0) ? "text-green-600" : "text-red-600"
                  )}>
                    {event.actual}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */
/* Event Popover Content (shared, desktop only)           */
/* ─────────────────────────────────────────────────────── */
function EventPopoverContent({ event }: { event: CalendarEvent }) {
  const isDividend = event.type === "dividend";
  const toneClasses = getEventToneClasses(event);

  const mockAsset = {
    type: event.assetType || "stock",
    symbol: event.assetSymbol || "",
    website: event.assetWebsite,
  };

  return (
    <PopoverContent className="w-72 p-4 shadow-xl">
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <AssetLogo asset={mockAsset} size="lg" showFallbackIcon className="rounded-lg" />
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-base leading-tight">{event.assetSymbol}</h4>
            <p className="text-xs text-muted-foreground truncate">{event.assetName}</p>
          </div>
          <Badge variant="outline" className={cn(
            "capitalize flex-shrink-0",
            toneClasses.badge
          )}>
            {getEventToneLabel(event)}
          </Badge>
        </div>

        <div className="space-y-2 py-2 border-y border-dashed">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center gap-1.5">
              <CalendarIcon className="h-3.5 w-3.5" /> Date
            </span>
            <span className="font-medium">{format(event.date, "PPP")}</span>
          </div>
          {!isDividend && hasMeaningfulTime(event.date) && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Time</span>
              <span className="font-medium">{formatEventTime(event.date)}</span>
            </div>
          )}

          {isDividend ? (
            <>
              {event.subTitle && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{event.subTitle}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Landmark className="h-3.5 w-3.5" /> Amount
                </span>
                <span className={cn("font-bold", toneClasses.amount)}>
                  {event.amount !== undefined 
                    ? `${event.amount.toFixed(4)} ${event.currency || "USD"}` 
                    : "N/A"}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Estimate EPS</span>
                <span className="font-medium">{event.estimate ?? "N/A"}</span>
              </div>
              {event.actual !== undefined && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Actual EPS</span>
                  <span className={cn(
                    "font-bold",
                    (event.actual ?? 0) >= (event.estimate ?? 0) ? "text-green-600" : "text-red-600"
                  )}>
                    {event.actual}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        
        <p className="text-[10px] text-muted-foreground italic">
          * Dates are indicative and subject to change by the company.
        </p>
      </div>
    </PopoverContent>
  );
}
